import type { BuyBridgeProduct, BuyBridgeVariant } from "@/lib/calculator/buy-bridge";
import { pickVariantForDose } from "@/lib/calculator/pick-variant-for-dose";

export const CALCULATOR_DIRECT_ADD_FLAG = "calculator-direct-add-v1";

export type CalculatorDirectAddVariant = "control" | "direct_add";

export function isBuyBridgeVariantAvailable(variant: BuyBridgeVariant): boolean {
	return variant.quantityAvailable === null || variant.quantityAvailable > 0;
}

export function isBuyBridgeVariantPurchasable(variant: BuyBridgeVariant): boolean {
	return (
		isBuyBridgeVariantAvailable(variant) &&
		typeof variant.grossAmount === "number" &&
		Number.isFinite(variant.grossAmount) &&
		variant.grossAmount >= 0
	);
}

/** Client-side guard for obvious automation; server-side commerce stays unaffected. */
export function isAutomatedExperimentVisitor(input: {
	webdriver?: boolean;
	userAgent?: string;
	isInternalUser?: boolean;
	isTestUser?: boolean;
}): boolean {
	return Boolean(
		input.webdriver ||
		input.isInternalUser ||
		input.isTestUser ||
		/(?:bot|crawler|spider|headless|lighthouse)/i.test(input.userAgent ?? ""),
	);
}

/**
 * Resolve the product variant shown by the Buy Bridge. Exact dose matches prefer the
 * smallest available pack. Larger-vial matches remain useful PDP recommendations but are
 * deliberately ineligible for direct add because silently changing the calculated vial
 * size would be misleading.
 */
export function resolveBuyBridgeMatch(
	product: NonNullable<BuyBridgeProduct>,
	peptideAmount: number,
): BuyBridgeVariant | null {
	const available = [...product.variants]
		.filter(isBuyBridgeVariantAvailable)
		.sort((a, b) => a.packCount - b.packCount);
	return pickVariantForDose(available, peptideAmount);
}

export function isCalculatorDirectAddEligible(
	product: NonNullable<BuyBridgeProduct>,
	matched: BuyBridgeVariant | null,
	peptideAmount: number,
): matched is BuyBridgeVariant {
	return (
		product.inStock &&
		matched !== null &&
		isBuyBridgeVariantPurchasable(matched) &&
		matched.doseMg === peptideAmount
	);
}
